import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/hooks/useClinic';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { UserPlus, Trash2, Loader2, Mail, Pencil, Check, X, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { formatDisplayName } from '@/lib/formatName';

interface ClinicMember {
  id: string;
  clinic_id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'staff';
  created_at: string;
  status: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  staff: 'Staff',
};

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-[hsl(var(--header-to))] text-white',
  admin: 'bg-primary text-primary-foreground',
  staff: 'bg-muted text-muted-foreground',
};

export default function SettingsTeam() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: clinic } = useClinic();
  const { role: myClinicRole, isLoading: roleLoading } = useMyClinicRole();
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'staff' });
  const [inviteLoading, setInviteLoading] = useState(false);

  // Remove confirmation state
  const [removeTarget, setRemoveTarget] = useState<ClinicMember | null>(null);

  const { data: members, isLoading } = useQuery({
    queryKey: ['clinic-members', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return [];
      const [membersResult, profilesResult] = await Promise.all([
        supabase
          .from('clinic_members')
          .select('*')
          .eq('clinic_id', profile.clinic_id)
          .order('created_at'),
        supabase
          .from('profiles')
          .select('full_name, email, credentials')
          .eq('clinic_id', profile.clinic_id),
      ]);
      if (membersResult.error) throw membersResult.error;

      // Build email -> profile map for credential lookups
      const profileMap = new Map<string, { full_name: string; credentials: string | null }>();
      (profilesResult.data ?? []).forEach((p) => {
        profileMap.set(p.email.toLowerCase(), { full_name: p.full_name, credentials: p.credentials });
      });

      return (membersResult.data ?? []).map((m) => {
        const matched = profileMap.get(m.email.toLowerCase());
        return {
          ...m,
          _displayName: matched
            ? formatDisplayName(matched.full_name, matched.credentials)
            : m.name,
        };
      }) as (ClinicMember & { _displayName: string })[];
    },
    enabled: !!profile?.clinic_id,
  });

  const activeMembers = members?.filter(m => m.status === 'active') ?? [];
  const pendingMembers = members?.filter(m => m.status === 'pending') ?? [];

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const { error } = await supabase
        .from('clinic_members')
        .update({ role })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-members'] });
      toast({ title: 'Role updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update role', description: error.message, variant: 'destructive' });
    },
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('clinic_members')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-members'] });
      toast({ title: 'Team member removed' });
      setRemoveTarget(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to remove member', description: error.message, variant: 'destructive' });
    },
  });

  const ownerCount = activeMembers.filter(m => m.role === 'owner').length;
  const currentUserEmail = profile?.email;
  const currentUserMember = activeMembers.find(m => 
    m.email?.toLowerCase() === currentUserEmail?.toLowerCase() ||
    m.name?.toLowerCase() === profile?.full_name?.toLowerCase()
  );
  const currentUserRole = currentUserMember?.role;
  const canEditEmails = currentUserRole === 'owner' || currentUserRole === 'admin';

  // Email update mutation
  const updateEmailMutation = useMutation({
    mutationFn: async ({ memberId, email }: { memberId: string; email: string }) => {
      const member = members?.find(m => m.id === memberId);
      const oldEmail = member?.email;

      if (oldEmail) {
        await supabase
          .from('profiles')
          .update({ email })
          .eq('email', oldEmail)
          .eq('clinic_id', profile!.clinic_id);
      }

      const { error: memberError } = await supabase
        .from('clinic_members')
        .update({ email })
        .eq('id', memberId);
      if (memberError) throw memberError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-members'] });
      setEditingEmailId(null);
      toast({ title: 'Email updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update email', description: error.message, variant: 'destructive' });
    },
  });

  function startEditEmail(memberId: string, currentEmail: string) {
    setEditingEmailId(memberId);
    setEditEmailValue(currentEmail);
  }

  function saveEmail(memberId: string) {
    const trimmed = editEmailValue.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: 'Please enter a valid email', variant: 'destructive' });
      return;
    }
    updateEmailMutation.mutate({ memberId, email: trimmed });
  }

  function handleRoleChange(memberId: string, memberEmail: string, newRole: string) {
    if (memberEmail === currentUserEmail) {
      toast({ title: 'Cannot change your own role', variant: 'destructive' });
      return;
    }
    const member = members?.find(m => m.id === memberId);
    if (member?.role === 'owner' && newRole !== 'owner' && ownerCount <= 1) {
      toast({ title: 'At least one owner must exist', variant: 'destructive' });
      return;
    }
    updateRoleMutation.mutate({ id: memberId, role: newRole });
  }

  function handleRemoveClick(member: ClinicMember) {
    if (member.email === currentUserEmail) {
      toast({ title: 'Cannot remove yourself', variant: 'destructive' });
      return;
    }
    if (member.role === 'owner' && ownerCount <= 1) {
      toast({ title: 'At least one owner must exist', variant: 'destructive' });
      return;
    }
    setRemoveTarget(member);
  }

  async function sendInviteEmail(email: string, name: string, role: string) {
    if (!clinic?.name || !profile?.clinic_id) return;
    
    const { error } = await supabase.functions.invoke('send-invite-email', {
      body: {
        email,
        name,
        clinic_name: clinic.name,
        clinic_id: profile.clinic_id,
        role,
      },
    });
    
    if (error) throw error;
  }

  // Invite new member
  async function handleInvite() {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      toast({ title: 'Name and email are required', variant: 'destructive' });
      return;
    }
    if (!profile?.clinic_id) return;

    const duplicate = members?.find(m => m.email.toLowerCase() === inviteForm.email.toLowerCase());
    if (duplicate) {
      toast({ title: 'This email is already on the team', variant: 'destructive' });
      return;
    }

    setInviteLoading(true);
    try {
      const { error } = await supabase
        .from('clinic_members')
        .insert({
          clinic_id: profile.clinic_id,
          name: inviteForm.name.trim(),
          email: inviteForm.email.trim().toLowerCase(),
          role: inviteForm.role,
        });
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['clinic-members'] });

      // Send invite email (non-blocking — member is already added as pending)
      try {
        await sendInviteEmail(
          inviteForm.email.trim().toLowerCase(),
          inviteForm.name.trim(),
          inviteForm.role,
        );
        toast({ title: `Invite sent to ${inviteForm.name}` });
      } catch (emailErr: any) {
        console.error('Failed to send invite email:', emailErr);
        toast({
          title: `${inviteForm.name} added to team`,
          description: 'Invite email could not be sent. You can resend it later.',
          variant: 'destructive',
        });
      }

      setInviteForm({ name: '', email: '', role: 'staff' });
      setInviteOpen(false);
    } catch (err: any) {
      toast({ title: 'Failed to add member', description: err.message, variant: 'destructive' });
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleResendInvite(member: ClinicMember) {
    setResendingId(member.id);
    try {
      await sendInviteEmail(member.email, member.name, member.role);
      toast({ title: `Invite resent to ${member.name}` });
    } catch (err: any) {
      toast({ title: 'Failed to resend invite', description: err.message, variant: 'destructive' });
    } finally {
      setResendingId(null);
    }
  }

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (myClinicRole === 'staff') {
    return <Navigate to="/settings/my-profile" replace />;
  }

  return (
    <div>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Team Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage clinic members and roles
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="w-full sm:w-auto bg-[hsl(var(--header-to))] hover:bg-[hsl(var(--header-from))] text-white"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Add Member
        </Button>
      </div>

      {/* Active Members Table */}
      <div className="rounded-md border border-gray-200 dark:border-border bg-white dark:bg-background overflow-x-auto">
        <Table className="min-w-[480px]">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-xs md:text-sm">Email</TableHead>
              <TableHead className="whitespace-nowrap" style={{ minWidth: '90px' }}>Role</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-24" /></TableCell>
                </TableRow>
              ))
            ) : (members ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  No team members found
                </TableCell>
              </TableRow>
            ) : (
              (members ?? []).map((member) => {
                const isCurrentUser = member.email === currentUserEmail;
                const isPending = member.status === 'pending' && !(isCurrentUser && member.role === 'owner');
                const isLastOwner = member.role === 'owner' && ownerCount <= 1;
                return (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium" title={member.name}>
                      <span className="flex items-center gap-2">
                        <span className="max-w-[160px] truncate">{(member as any)._displayName ?? member.name}</span>
                        {isCurrentUser && (
                          <Badge variant="secondary" className="text-xs">
                            You
                          </Badge>
                        )}
                        {isPending && (
                          <Badge className="text-xs bg-amber-500 text-white border-0">
                            Pending
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs md:text-sm">
                      {!isPending && editingEmailId === member.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editEmailValue}
                            onChange={(e) => setEditEmailValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEmail(member.id);
                              if (e.key === 'Escape') setEditingEmailId(null);
                            }}
                            className="h-8 w-48 border-gray-200 dark:border-border bg-white dark:bg-background"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-primary hover:text-primary" onClick={() => saveEmail(member.id)} disabled={updateEmailMutation.isPending}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setEditingEmailId(null)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="flex items-center gap-2 group">
                          {member.email}
                          {!isPending && canEditEmails && (
                            <button
                              onClick={() => startEditEmail(member.id, member.email)}
                              className="inline-flex items-center justify-center h-6 w-6 rounded border border-muted-foreground/30 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                              title="Edit email"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap" style={{ minWidth: '90px' }}>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[member.role]}`}>
                        {ROLE_LABELS[member.role]}
                      </span>
                    </TableCell>
                    <TableCell>
                      {isPending ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendInvite(member)}
                          disabled={resendingId === member.id}
                        >
                          {resendingId === member.id ? (
                            <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Sending...</>
                          ) : (
                            <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Resend Invite</>
                          )}
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleRoleChange(member.id, member.email, value)}
                            disabled={isCurrentUser || isLastOwner}
                          >
                            <SelectTrigger className="w-28 border-gray-200 dark:border-border bg-white dark:bg-background">
                               <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">Owner</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="staff">Staff</SelectItem>
                            </SelectContent>
                          </Select>

                          {!isCurrentUser && !isLastOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveClick(member)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Active member count */}
      {activeMembers.length > 0 && (
        <p className="text-xs text-muted-foreground mt-3">
          {activeMembers.length} active team member{activeMembers.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Invite a new member to your clinic. They'll receive an email to set up their account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="invite-name">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="invite-name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Jane Smith"
                className="border-gray-200 dark:border-border bg-white dark:bg-background"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="invite-email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane@yourclinic.com"
                className="border-gray-200 dark:border-border bg-white dark:bg-background"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(v) => setInviteForm(f => ({ ...f, role: v }))}
              >
                <SelectTrigger className="text-left border-gray-200 dark:border-border bg-white dark:bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">
                    <div>
                      <p className="font-medium">Owner</p>
                      <p className="text-xs text-muted-foreground">Full access, manage billing and settings</p>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div>
                      <p className="font-medium">Admin</p>
                      <p className="text-xs text-muted-foreground">Manage policies and team members</p>
                    </div>
                  </SelectItem>
                  <SelectItem value="staff">
                    <div>
                      <p className="font-medium">Staff</p>
                      <p className="text-xs text-muted-foreground">View and acknowledge policies</p>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviteLoading}>
              {inviteLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending Invite...</>
              ) : (
                <><Mail className="mr-2 h-4 w-4" /> Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {removeTarget?.name} ({removeTarget?.email}) from your clinic.
              They will no longer have access to Veracord.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeTarget && removeMutation.mutate(removeTarget.id)}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {removeMutation.isPending ? 'Removing...' : 'Remove Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
